import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cloud, FileText, X, Check, Loader2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

const ACCEPT = ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'];
const MAX = 15 * 1024 * 1024;

export function ReferenceUploader({ files, setFiles }) {
  const input = useRef();
  const [over, setOver] = useState(false);

  const add = (list) => {
    const next = [];
    Array.from(list).forEach((file) => {
      if (!ACCEPT.includes(file.type)) { toast.error(`${file.name}: only PNG, JPG, WEBP or PDF`); return; }
      if (file.size > MAX) { toast.error(`${file.name} is over 15MB`); return; }
      next.push({ id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`, file, url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null, status: 'ready' });
    });
    if (files.length + next.length > 10) { toast.error('Maximum 10 reference files'); return; }
    setFiles((f) => [...f, ...next]);
  };
  const remove = (id) => setFiles((f) => { const t = f.find((x) => x.id === id); if (t?.url) URL.revokeObjectURL(t.url); return f.filter((x) => x.id !== id); });

  return (
    <div data-testid="reference-uploader">
      <label className="field-label flex items-center gap-2">Drop your references into the cloud <Cloud size={12} /></label>
      <motion.div role="button" tabIndex={0} onClick={() => input.current.click()} onKeyDown={(e) => e.key === 'Enter' && input.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
        animate={{ scale: over ? 1.02 : 1 }} className={`relative cloud-shape glass px-8 py-12 text-center transition-colors duration-300 ${over ? 'bg-white/25' : ''}`} style={{ boxShadow: over ? 'inset 0 0 60px 10px rgba(255,255,255,0.4), 0 0 50px rgba(249,115,22,0.3)' : 'inset 0 0 50px 8px rgba(255,255,255,0.22)' }} data-testid="upload-dropzone" data-cursor="cloud">
        <input ref={input} type="file" multiple accept=".png,.jpg,.jpeg,.webp,.pdf,image/png,image/jpeg,image/webp,application/pdf" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} data-testid="upload-input" />
        <UploadCloud size={30} className="mx-auto text-white drop-shadow-[0_0_14px_rgba(255,255,255,0.7)]" />
        <p className="font-display text-sm sm:text-base font-semibold mt-4 uppercase tracking-[0.15em]">Drop your references into the cloud</p>
        <p className="mt-3 text-xs text-white/65 max-w-md mx-auto">Upload your inspiration, logos, examples, screenshots, color references or previous designs.</p>
        <p className="mt-2 text-[0.65rem] tracking-[0.25em] uppercase text-white/45">PNG · JPG · JPEG · WEBP · PDF · up to 10 files</p>
      </motion.div>

      <AnimatePresence>
        {files.length > 0 && (
          <motion.ul className="mt-5 flex flex-wrap gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="upload-file-list">
            <AnimatePresence>
              {files.map((f, i) => (
                <motion.li key={f.id} layout initial={{ opacity: 0, y: 16, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.6, filter: 'blur(8px)' }} transition={{ type: 'spring', stiffness: 220, damping: 20 }}
                  className="glass relative cloud-shape-2 flex items-center gap-3 pl-3 pr-10 py-2.5 float-slow" style={{ animationDelay: `${i * -1.3}s` }} data-testid={`upload-file-${i}`}>
                  {f.url ? <img src={f.url} alt="" className="h-10 w-10 rounded-xl object-cover" /> : <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center"><FileText size={16} /></span>}
                  <div className="min-w-0">
                    <p className="text-xs font-medium truncate max-w-[140px]" data-testid={`upload-filename-${i}`}>{f.file.name}</p>
                    <p className="text-[0.6rem] tracking-[0.2em] uppercase text-white/55 flex items-center gap-1" data-testid={`upload-status-${i}`}>
                      {f.status === 'uploading' && <Loader2 size={10} className="animate-spin" />}{f.status === 'sent' && <Check size={10} className="text-[#ffb37a]" />}
                      {f.status === 'ready' ? `${Math.max(1, Math.round(f.file.size / 1024))} KB · ready` : f.status}
                    </p>
                  </div>
                  <button type="button" onClick={() => remove(f.id)} disabled={f.status === 'uploading'} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-white/60 hover:text-white hover:bg-white/15 transition-colors" aria-label={`Remove ${f.file.name}`} data-testid={`upload-remove-${i}`}><X size={13} /></button>
                </motion.li>
              ))}
            </AnimatePresence>
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
